import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useQueryState } from "nuqs";
import { useMemo } from "react";
import { NavLink } from "react-router-dom";

import { Link } from "@transfr-inc/dashboard-components";
import {
  BasicCell,
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
} from "@transfr-inc/dashboard-components/data-table";
import {
  courseEnum,
  formatDateDigits,
  formatTime,
  truncateNumber,
} from "@transfr-inc/dashboard-components/utils";

import { ActivityStatusCell } from "@components/table-cells/status-cell";
import { createActivityPlayerLink } from "@lib/utils/navigation.utils";
import { MasteryStatus } from "@models/mastery";
import { OohStatus } from "@models/ooh-status";
import { getContentTypeIconById } from "@transfr-inc/dashboard-components/curriculum";

import { listOfExemptedSims } from "./course.utils";

import "./lessons-masteries.table.scss";

export default function LessonsMasteriesTable({
  lesson,
  responsiveStyles,
  courseId,
  courseName,
  unitName,
  unitIndex,
  unitId,
  classroomId,
  courseData,
  lessonIndex,
  prereqEnabled = true,
  isTraineePage = false,
}) {
  const [previewParam] = useQueryState("preview");

  const columns = [
    new Column(
      "title",
      "Activity",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, row, index) => {
          if (isTraineePage) {
            return (
              <div className="activity-title-icon-cell">
                <div
                  className={clsx(
                    "activity-icon",
                    "trainee-page",
                    `content-type-${row.contentType}`
                  )}
                >
                  {row?.contentType &&
                    getContentTypeIconById(row.contentType, row.mimeType)}
                </div>
                <span className="activity-title-text">{value}</span>
              </div>
            );
          }
          return (
            <div className="activity-title-icon-cell">
              <div
                className={clsx(
                  "activity-icon",
                  `content-type-${row.contentType}`
                )}
              >
                {row?.contentType &&
                  getContentTypeIconById(row.contentType, row.mimeType)}
              </div>
              {row.locked ? (
                <span>{value}</span>
              ) : (
                <Link
                  routerComponent={NavLink}
                  className="activity-title"
                  to={createActivityPlayerLink({
                    lessonId: row?.lessonId,
                    ...(previewParam && { search: { preview: classroomId } }),
                    state: {
                      classroomId,
                      courseId,
                      unitId,
                      activityIndex: index,
                      prerequisitesEnabled: courseData?.prerequisitesEnabled,
                      courseData,
                    },
                  })}
                >
                  {value}
                </Link>
              )}
            </div>
          );
        },
      })
    ),
    new Column(
      "time",
      "Time",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, _) => {
          const formattedTime = formatTime(Number.parseFloat(value), {
            withSeconds: false,
          });
          return <BasicCell value={formattedTime} />;
        },
      })
    ),
    new Column(
      "status",
      "Status",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: false,
        customFunction: (value, row) => {
          return (
            <div>
              {value ? (
                <>
                  <ActivityStatusCell status={value} row={row} />
                </>
              ) : (
                <BasicCell />
              )}
            </div>
          );
        },
      })
    ),
    new Column(
      "score",
      "Score",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, row) => {
          if (row?.displayScore === false && row?.attempts > 0) {
            return <div className="mastery-cell">N/A</div>;
          }
          return (
            <div className="mastery-cell">
              {value && !Number.isNaN(Number(value)) ? (
                <div className="mastery-value">{truncateNumber(value)}%</div>
              ) : (
                <BasicCell />
              )}
            </div>
          );
        },
      })
    ),
    new Column(
      "attempts",
      "Attempts",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, _) => {
          return <div>{value ? <div>{value}</div> : <BasicCell />}</div>;
        },
      })
    ),
    new Column(
      "completed",
      "Complete",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        sortKeyFunctions: [(row) => row.completionDate ?? ""],
        customFunction: (_value, row) => {
          const completionDate = row.completionDate;
          const formattedDate = completionDate
            ? formatDateDigits(completionDate)
            : "";

          const isFailed = row?.status === OohStatus.FAILED;
          return (
            <div className={clsx("completed-cell", isFailed && "failed")}>
              {completionDate ? (
                <>
                  {!isFailed && (
                    <FontAwesomeIcon icon="fa-solid fa-circle-check"></FontAwesomeIcon>
                  )}
                  {formattedDate}
                </>
              ) : (
                <BasicCell
                  value={
                    listOfExemptedSims.includes(row?.simulationId) ? "N/A" : ""
                  }
                />
              )}
            </div>
          );
        },
      })
    ),
    new Column(
      "latestTimestamp",
      "Last Attempted",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, row) => {
          const hasAttempted =
            row.status && row.status !== MasteryStatus.NotStarted;
          const formattedDate =
            hasAttempted && value ? formatDateDigits(value) : undefined;
          return <BasicCell value={formattedDate} />;
        },
      })
    ),
  ];

  const tableData = useMemo(() => {
    if (!lesson || !lesson.activities) return [];

    return lesson.activities.map((activity) => {
      const breadcrumb = [
        {
          type: courseEnum.course,
          name: courseName,
          index: 0,
        },
        ...(unitIndex === null
          ? []
          : [
              {
                type: courseEnum.unit,
                name: unitName,
                index: unitIndex,
              },
            ]),
        {
          type: courseEnum.lesson,
          name: lesson.title,
          index: lessonIndex,
        },
      ];

      return {
        ...activity,
        locked: prereqEnabled && (activity.locked ?? false),
        status: activity.masteryStatus,
        time: activity.timeSpent ?? 0,
        attempts: activity.totalAttempts ?? 0,
        completed: activity.completed ?? false,
        completionDate: activity.completionDate ?? "",
        lessonId: lesson.id,
        score: activity.masteryScore ?? undefined,
        displayScore: activity.displayScore,
        contentType: activity.contentType,
        latestTimestamp: activity.latestTimestamp ?? undefined,
        breadcrumb,
      };
    });
  }, [lesson, prereqEnabled, courseName, unitName, unitIndex, lessonIndex]);

  return (
    <DataTable
      className={clsx("lessons-masteries-table", responsiveStyles)}
      columns={columns}
      data={tableData}
      keyFields={["id"]}
      responsiveStyles={responsiveStyles}
      rowHeightConfig={{ large: 54, medium: 160, small: 280 }}
      isRowDisabled={(row) =>
        row.locked == true || row.status === MasteryStatus.Exempt
      }
    />
  );
}
